import React, { useState } from "react";
import styled from "styled-components";
import { quizColors } from "@ui/styles/colors";
import { ColorSelector } from "@ui/components/inputs/colorSelector";
import { Datalist } from "@ui/components/inputs/datalist";
import { DatePicker } from "@ui/components/inputs/datePicker";
import { FileInput } from "@ui/components/inputs/fileInput";
import { MaterialTextInput } from "@ui/components/inputs/materialTextInput";
import { RadioButton } from "@ui/components/inputs/radio";
import { RangePicker } from "@ui/components/inputs/rangePicker";
import { Selecter } from "@ui/components/inputs/selecter";
import { SimpleTextInput } from "@ui/components/inputs/simpleTextInput";
import { TagsInput } from "@ui/components/inputs/tagsInput/tagsInput";
import { TagsView } from "@ui/components/inputs/tagsView";
import { NormalInput, RoundedTextArea, RoundedTextInput, TextInput } from "@ui/components/inputs/textInput";
import { Toggle } from "@ui/components/inputs/toggle";
import { AdvancedDatePicker } from "@ui/components/inputs/advancedTimePicker";
import { Card, CardTitle, Caption, Row, Section, SectionIntro, SectionTitle, Stack } from "./debugUi";

const selectOptions = [
	{ value: "design", label: "Design" },
	{ value: "cours", label: "Cours" },
	{ value: "live", label: "Live" },
];

export const InputsSection: React.FC = () => {
	const [simple, setSimple] = useState("");
	const [password, setPassword] = useState("");
	const [multiline, setMultiline] = useState("Plusieurs lignes");
	const [primaryText, setPrimaryText] = useState("");
	const [secondaryText, setSecondaryText] = useState("");
	const [text, setText] = useState("TextInput");
	const [normal, setNormal] = useState("NormalInput");
	const [rounded, setRounded] = useState("Rounded");
	const [area, setArea] = useState("Zone de texte");
	const [material, setMaterial] = useState("Material");
	const [toggled, setToggled] = useState(true);
	const [radio, setRadio] = useState("a");
	const [checked, setChecked] = useState(false);
	const [selected, setSelected] = useState("design");
	const [date, setDate] = useState<Date | null>(new Date(1996, 5, 15));
	const [range, setRange] = useState(40);
	const [fileName, setFileName] = useState("Aucun fichier");
	const [color, setColor] = useState<string>(quizColors[0]);
	const [tags, setTags] = useState<string[]>(["design", "cours"]);
	const [selectedTag, setSelectedTag] = useState("design");
	const [listValue, setListValue] = useState("");
	const [startDate, setStartDate] = useState<Date | undefined>(new Date());
	const [endDate, setEndDate] = useState<Date | undefined>(new Date());

	return (
		<Section id="inputs">
			<SectionTitle>Inputs</SectionTitle>
			<SectionIntro>Composants qui se rendent avec un état local, sans appel réseau.</SectionIntro>

			<Card>
				<CardTitle>SimpleTextInput</CardTitle>
				<Row>
					<Stack>
						<Caption>défaut</Caption>
						<SimpleTextInput value={simple} onChange={setSimple} placeholder="Écrire" />
					</Stack>
					<Stack>
						<Caption>erreur</Caption>
						<SimpleTextInput value="invalide" onChange={() => undefined} error="Champ requis" />
					</Stack>
					<Stack>
						<Caption>mot de passe</Caption>
						<SimpleTextInput value={password} onChange={setPassword} type="password" placeholder="Mot de passe" />
					</Stack>
				</Row>
				<Row>
					<Stack>
						<Caption>multiline</Caption>
						<SimpleTextInput value={multiline} onChange={setMultiline} multiline />
					</Stack>
					<Stack>
						<Caption>primary</Caption>
						<SimpleTextInput value={primaryText} onChange={setPrimaryText} color="primary" placeholder="Primary" />
					</Stack>
					<Stack>
						<Caption>secondary</Caption>
						<SimpleTextInput
							value={secondaryText}
							onChange={setSecondaryText}
							color="secondary"
							placeholder="Secondary"
						/>
					</Stack>
				</Row>
			</Card>

			<Card>
				<CardTitle>Champs texte</CardTitle>
				<Row>
					<Stack>
						<Caption>TextInput</Caption>
						<TextInput value={text} onChange={(event) => setText(event.target.value)} />
					</Stack>
					<Stack>
						<Caption>NormalInput</Caption>
						<NormalInput value={normal} onChange={(event) => setNormal(event.target.value)} />
					</Stack>
					<Stack>
						<Caption>RoundedTextInput</Caption>
						<RoundedTextInput value={rounded} onChange={(event) => setRounded(event.target.value)} />
					</Stack>
					<Stack>
						<Caption>RoundedTextArea</Caption>
						<RoundedTextArea value={area} onChange={(event) => setArea(event.target.value)} />
					</Stack>
					<Stack>
						<Caption>MaterialTextInput</Caption>
						<MaterialTextInput value={material} onChange={setMaterial} placeholder="Libellé" />
					</Stack>
				</Row>
			</Card>

			<Card>
				<CardTitle>Toggle, radio, select</CardTitle>
				<Row>
					<Stack>
						<Caption>on</Caption>
						<Toggle toggled={toggled} onChange={setToggled} />
					</Stack>
					<Stack>
						<Caption>off</Caption>
						<Toggle toggled={false} onChange={() => undefined} />
					</Stack>
					<Stack>
						<Caption>disabled</Caption>
						<Toggle toggled onChange={() => undefined} disabled />
					</Stack>
				</Row>
				<Row>
					<RadioButton
						id="debug-radio-a"
						name="debug-radio"
						value="a"
						label="Option A"
						checked={radio === "a"}
						onChange={setRadio}
					/>
					<RadioButton
						id="debug-radio-b"
						name="debug-radio"
						value="b"
						label="Option B"
						checked={radio === "b"}
						onChange={setRadio}
					/>
					<RadioButton
						id="debug-check"
						name="debug-check"
						checkbox
						value="ok"
						label="Case"
						checked={checked}
						onChange={() => setChecked((current) => !current)}
					/>
				</Row>
				<Stack>
					<Caption>Selecter</Caption>
					<Selecter value={selected} onChange={setSelected} options={selectOptions} placeholder="Choisir" />
				</Stack>
			</Card>

			<Card>
				<CardTitle>Dates et range</CardTitle>
				<DatePicker value={date} onChange={setDate} minYear={1980} hideMonth={false} hideDay={false} label="Date" />
				<Stack>
					<Caption>RangePicker {range}</Caption>
					<RangePicker value={range} onChange={setRange} min={0} max={100} step={1} />
				</Stack>
				<AdvancedDatePicker
					type="HOURS_RANGE"
					startDate={startDate}
					endDate={endDate}
					onChangeStart={(next) => setStartDate(next ?? undefined)}
					onChangeEnd={(next) => setEndDate(next ?? undefined)}
				/>
			</Card>

			<Card>
				<CardTitle>Fichier, couleur, tags</CardTitle>
				<DropZone>
					<FileInput onChange={(files) => setFileName(files?.[0]?.name ?? "Aucun fichier")}>
						{(dragging) => <DropLabel $dragging={dragging}>{dragging ? "Déposer" : fileName}</DropLabel>}
					</FileInput>
				</DropZone>
				<ColorSelector color={color} colors={quizColors} onColorChange={setColor} />
				<TagsInput tags={tags} setTags={setTags} suggestionsDisabled />
				<TagsView
					tags={["design", "cours", "live"]}
					selectedTag={selectedTag}
					withoutTranslation
					onItemClick={setSelectedTag}
				/>
				<Datalist
					id="debug-datalist"
					value={listValue}
					onChange={setListValue}
					placeholder="Liste"
					options={["Paris", "Dakar", "Lyon"]}
				/>
			</Card>
		</Section>
	);
};

const DropZone = styled.div`
	height: 72px;
	border: 1px dashed ${({ theme }) => theme.colors.gray};
	border-radius: 10px;
`;

const DropLabel = styled.div<{ $dragging: boolean }>`
	height: 72px;
	display: flex;
	align-items: center;
	justify-content: center;
	color: ${({ theme, $dragging }) => ($dragging ? theme.colors.primary : theme.colors.textSecondary)};
`;
